/**
 * VL Chat Wrapper Component using assistant-ui
 *
 * This component encapsulates ALL VL Chat functionality including:
 * - Thread management (with context)
 * - AssistantRuntime provider (using useExternalStoreRuntime)
 * - Message state management
 * - Navigation action handling via tool calls
 * - Tool call UI display (TODO: implement visual tool call rendering)
 *
 * This keeps all VL Chat logic contained and makes it easy to disable
 * the entire feature by commenting out this wrapper in App.tsx
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  ThreadMessageLike,
  AppendMessage,
} from "@assistant-ui/react";
import { v4 as uuidv4 } from "uuid";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { VL_CHAT } from "helpers/constants/filters";
import { useParams, useNavigate } from "react-router";
import { ToolStatus } from "./ToolStatusBar";

// ============================================================================
// Types
// ============================================================================

interface ThreadContextType {
  threadId: string | undefined;
  createNewThread: () => void;
  datasetId: string | undefined;
}

interface ToolStatusContextType {
  toolStatuses: Map<string, ToolStatus>;
  showToolStatus: boolean;
  isThinking: boolean;
}

interface ToolCallPart {
  type: "tool-call";
  toolCallId: string;
  toolName: string;
  args: Record<string, string | number | boolean | null>;
}

interface TextPart {
  type: "text";
  text: string;
}

type ContentPart = TextPart | ToolCallPart;

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: ContentPart[];
  createdAt?: Date;
}

// ============================================================================
// Thread Context
// ============================================================================

const ThreadContext = createContext<ThreadContextType | undefined>(undefined);
const ToolStatusContext = createContext<ToolStatusContextType | undefined>(
  undefined,
);

/**
 * Hook to access thread management functionality
 * Must be used within VLChatWrapper
 */
export const useVLChatThread = () => {
  const context = useContext(ThreadContext);
  if (!context) {
    throw new Error("useVLChatThread must be used within VLChatWrapper");
  }
  return context;
};

/**
 * Hook to access tool status for rendering in UI
 * Must be used within VLChatWrapper
 */
export const useToolStatus = () => {
  const context = useContext(ToolStatusContext);
  if (!context) {
    throw new Error("useToolStatus must be used within VLChatWrapper");
  }
  return context;
};

// ============================================================================
// Navigation Handler
// ============================================================================

/**
 * Handle navigation tool calls from the assistant
 */
function useNavigationHandler() {
  const navigate = useNavigate();

  const handleNavigateToExploration = useCallback(
    (args: Record<string, unknown>) => {
      const {
        path,
        entity_type,
        granularity,
        vql,
        q,
        search_text_threshold,
        semantic_relevance_threshold,
        flat_response,
        auto_flat_response,
      } = args;

      // Build query parameters
      const params = new URLSearchParams();
      params.set("page", "1");

      if (entity_type) params.set("t", entity_type as string);
      if (granularity) params.set("granularity", granularity as string);
      if (vql) params.set("vql", vql as string);
      if (q) params.set("q", q as string);
      if (search_text_threshold)
        params.set("search_text_threshold", search_text_threshold as string);
      if (semantic_relevance_threshold)
        params.set(
          "semantic_relevance_threshold",
          semantic_relevance_threshold as string,
        );
      if (flat_response) params.set("flat_response", flat_response as string);
      if (auto_flat_response)
        params.set("auto_flat_response", auto_flat_response as string);

      const queryString = params.toString();
      const fullUrl = queryString ? `${path}?${queryString}` : (path as string);

      console.log("[VLChatNavigateAction] Navigating to:", fullUrl);

      // Validate path
      if (typeof path !== "string" || !path.startsWith("/dataset/")) {
        console.error(
          "[VLChatNavigateAction] Invalid path - must start with /dataset/",
        );
        return;
      }

      navigate(fullUrl);
    },
    [navigate],
  );

  return { handleNavigateToExploration };
}

// ============================================================================
// Message Conversion
// ============================================================================

/**
 * Convert our message format to assistant-ui ThreadMessageLike format
 */
const convertMessage = (message: Message): ThreadMessageLike => {
  // For simplicity, we only support text content in the UI.
  // Tool calls are handled separately via streaming markers.
  const textParts = message.content
    .filter((part): part is TextPart => part.type === "text")
    .map((part) => ({ type: "text" as const, text: part.text }));

  // If no text parts, return empty content
  const content =
    textParts.length > 0 ? textParts : [{ type: "text" as const, text: "" }];

  return {
    role: message.role,
    content,
    id: message.id,
    createdAt: message.createdAt,
  };
};

// ============================================================================
// API Client
// ============================================================================

/**
 * Build the VL Chat API URL using the same pattern as other API calls
 */
function getVLChatApiUrl(endpoint: string = "stream"): string {
  const hostUrl = process.env.REACT_APP_API_ENDPOINT || "";
  return `${hostUrl}/api/vl-chat/${endpoint}`;
}

/**
 * Fetch chat history for a thread from the backend
 */
async function fetchChatHistory(
  threadId: string,
  datasetId: string | undefined,
): Promise<Message[]> {
  const params = new URLSearchParams({ thread_id: threadId });
  if (datasetId) {
    params.set("dataset_id", datasetId);
  }

  const response = await fetch(`${getVLChatApiUrl("history")}?${params}`, {
    method: "GET",
    credentials: "include", // Include session cookie
  });

  if (!response.ok) {
    console.warn("[VLChat] Failed to fetch history:", response.statusText);
    return [];
  }

  const data = await response.json();
  const messages: Message[] = [];

  for (const msg of data.messages || []) {
    // Convert backend format to our Message format
    const content: ContentPart[] = [];
    for (const part of msg.content || []) {
      if (part.type === "text" && part.text) {
        content.push({ type: "text", text: part.text });
      } else if (part.type === "tool-call") {
        content.push({
          type: "tool-call",
          toolCallId: part.toolCallId || "",
          toolName: part.toolName || "",
          args: part.args || {},
        });
      }
    }

    if (content.length > 0) {
      messages.push({
        id: msg.id || uuidv4(),
        role: msg.role,
        content,
      });
    }
  }

  return messages;
}

/**
 * Send messages to the backend and handle SSE streaming response
 */
async function* streamChat(
  messages: Message[],
  threadId: string,
  datasetId: string | undefined,
): AsyncGenerator<string> {
  const response = await fetch(getVLChatApiUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include", // Include session cookie
    body: JSON.stringify({
      messages: messages.map((m) => ({
        role: m.role,
        content: m.content.map((part) => {
          if (part.type === "text") {
            return { type: "text", text: part.text };
          }
          if (part.type === "tool-call") {
            return {
              type: "tool-call",
              toolCallId: part.toolCallId,
              toolName: part.toolName,
              args: part.args,
            };
          }
          return part;
        }),
      })),
      thread_id: threadId,
      dataset_id: datasetId,
    }),
  });

  if (!response.ok) {
    throw new Error(`Chat request failed: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Process complete lines from buffer
    const lines = buffer.split("\n");
    // Keep the last incomplete line in buffer
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (!line.trim()) continue;

      // Parse Vercel AI SDK Data Stream Protocol format
      // Format: "TYPE:JSON_DATA" where TYPE is a single character or prefix
      // 0: text delta (e.g., '0:"Hello"')
      // b: tool call begin (e.g., 'b:{"toolCallId":"...","toolName":"..."}')
      // c: tool call delta
      // a: tool result

      // Text delta: 0:"text content"
      if (line.startsWith("0:")) {
        try {
          const textDelta = JSON.parse(line.slice(2));
          if (typeof textDelta === "string") {
            yield textDelta;
          }
        } catch {
          console.warn("[VLChat] Failed to parse text delta:", line);
        }
        continue;
      }

      // Tool call begin: b:{"toolCallId":"...","toolName":"..."}
      if (line.startsWith("b:")) {
        try {
          const toolCall = JSON.parse(line.slice(2));
          // Store tool call info for later processing
          // Use unique end markers to avoid regex issues with JSON containing ]
          yield `\n<<TOOL_CALL_BEGIN>>${JSON.stringify(toolCall)}<</TOOL_CALL_BEGIN>>\n`;
        } catch {
          console.warn("[VLChat] Failed to parse tool call begin:", line);
        }
        continue;
      }

      // Tool call delta: c:{"toolCallId":"...","argsTextDelta":"..."}
      if (line.startsWith("c:")) {
        try {
          const delta = JSON.parse(line.slice(2));
          yield `\n<<TOOL_CALL_DELTA>>${JSON.stringify(delta)}<</TOOL_CALL_DELTA>>\n`;
        } catch {
          console.warn("[VLChat] Failed to parse tool call delta:", line);
        }
        continue;
      }

      // Tool result: a:{"toolCallId":"...","result":"..."}
      if (line.startsWith("a:")) {
        try {
          const toolResult = JSON.parse(line.slice(2));
          yield `\n<<TOOL_RESULT>>${JSON.stringify(toolResult)}<</TOOL_RESULT>>\n`;
        } catch {
          console.warn("[VLChat] Failed to parse tool result:", line);
        }
        continue;
      }

      // Error: 3:"error message"
      if (line.startsWith("3:")) {
        try {
          const error = JSON.parse(line.slice(2));
          console.error("[VLChat] Stream error:", error);
        } catch {
          console.warn("[VLChat] Failed to parse error:", line);
        }
        continue;
      }

      // Fallback: try to parse as SSE format for backwards compatibility
      if (line.startsWith("data: ")) {
        const data = line.slice(6);
        if (data === "[DONE]") continue;

        try {
          const parsed = JSON.parse(data);
          if (parsed.type === "text-delta" && parsed.textDelta) {
            yield parsed.textDelta;
          }
          if (parsed.type === "tool-call") {
            yield `\n[TOOL_CALL:${JSON.stringify(parsed)}]\n`;
          }
        } catch {
          if (data.trim()) {
            yield data;
          }
        }
      }
    }
  }
}

// ============================================================================
// VLChat Runtime Provider
// ============================================================================

interface VLChatRuntimeProviderProps {
  children: React.ReactNode;
  threadId: string;
  datasetId: string | undefined;
}

const VLChatRuntimeProvider: React.FC<VLChatRuntimeProviderProps> = ({
  children,
  threadId,
  datasetId,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [toolStatuses, setToolStatuses] = useState<Map<string, ToolStatus>>(
    new Map(),
  );
  const [showToolStatus, setShowToolStatus] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const hideToolStatusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { handleNavigateToExploration } = useNavigationHandler();

  // Helper to update tool status
  const updateToolStatus = useCallback((statusData: ToolStatus) => {
    // Clear thinking state when first tool status arrives
    setIsThinking(false);

    setToolStatuses((prev) => {
      const newMap = new Map(prev);
      newMap.set(statusData.toolName, statusData);
      return newMap;
    });

    // Show status bar when tools start running
    if (statusData.status === "running") {
      setShowToolStatus(true);
      // Clear any pending hide timeout
      if (hideToolStatusTimeoutRef.current) {
        clearTimeout(hideToolStatusTimeoutRef.current);
        hideToolStatusTimeoutRef.current = null;
      }
    }
  }, []);

  // Helper to hide tool status immediately
  const hideToolStatus = useCallback(() => {
    if (hideToolStatusTimeoutRef.current) {
      clearTimeout(hideToolStatusTimeoutRef.current);
      hideToolStatusTimeoutRef.current = null;
    }
    setShowToolStatus(false);
    setToolStatuses(new Map());
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideToolStatusTimeoutRef.current) {
        clearTimeout(hideToolStatusTimeoutRef.current);
      }
    };
  }, []);

  // Load chat history when threadId changes
  useEffect(() => {
    let cancelled = false;

    // Clear messages immediately when thread changes to avoid showing stale messages
    // This is critical because we removed key={threadId} to prevent full app remounts
    setMessages([]);
    setToolStatuses(new Map());
    setShowToolStatus(false);
    setIsRunning(false);
    setIsThinking(false);

    const loadHistory = async () => {
      try {
        console.log("[VLChat] Loading history for thread:", threadId);
        const history = await fetchChatHistory(threadId, datasetId);
        if (!cancelled) {
          console.log(
            "[VLChat] Loaded",
            history.length,
            "messages from history",
          );
          setMessages(history);
        }
      } catch (error) {
        console.error("[VLChat] Failed to load history:", error);
        if (!cancelled) {
          setMessages([]);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [threadId, datasetId]);

  const onNew = useCallback(
    async (message: AppendMessage) => {
      // Extract text from user message - AppendMessage.content is an array of content parts
      // Check the first element which is typically the text content
      const firstContent = message.content[0];
      if (!firstContent || firstContent.type !== "text") {
        throw new Error("Only text messages are supported");
      }

      const userMessage: Message = {
        id: uuidv4(),
        role: "user",
        content: [{ type: "text", text: firstContent.text }],
        createdAt: new Date(),
      };

      // Add user message to state
      setMessages((prev) => [...prev, userMessage]);

      // Start streaming response
      setIsRunning(true);
      setIsThinking(true);

      const assistantId = uuidv4();
      const assistantMessage: Message = {
        id: assistantId,
        role: "assistant",
        content: [{ type: "text", text: "" }],
        createdAt: new Date(),
      };

      // Add placeholder assistant message
      setMessages((prev) => [...prev, assistantMessage]);

      try {
        let fullText = "";
        const pendingToolCalls: Record<
          string,
          { toolName: string; args: string }
        > = {};

        // Only send the new user message - checkpointer has the full history
        // This follows the official LangGraph + assistant-ui pattern where
        // the backend checkpointer is the source of truth for conversation state
        for await (const chunk of streamChat(
          [userMessage],
          threadId,
          datasetId,
        )) {
          // Check for tool status updates
          if (chunk.includes("<<TOOL_STATUS>>")) {
            const match = chunk.match(/<<TOOL_STATUS>>(.+?)<<\/TOOL_STATUS>>/);
            if (match) {
              try {
                const statusData = JSON.parse(match[1]);
                updateToolStatus({
                  toolName: statusData.tool_name,
                  displayName: statusData.display_name,
                  status: statusData.status,
                  message: statusData.message,
                });
              } catch {
                // Ignore parsing errors
              }
            }
            continue;
          }

          // Check for tool call begin (new format with unique markers)
          if (chunk.includes("<<TOOL_CALL_BEGIN>>")) {
            const match = chunk.match(
              /<<TOOL_CALL_BEGIN>>(.+?)<<\/TOOL_CALL_BEGIN>>/,
            );
            if (match) {
              try {
                const toolCall = JSON.parse(match[1]);
                // Store pending tool call
                pendingToolCalls[toolCall.toolCallId] = {
                  toolName: toolCall.toolName,
                  args: "",
                };
              } catch {
                // Ignore parsing errors
              }
            }
            continue;
          }

          // Check for tool call delta (accumulate args)
          if (chunk.includes("<<TOOL_CALL_DELTA>>")) {
            const match = chunk.match(
              /<<TOOL_CALL_DELTA>>(.+?)<<\/TOOL_CALL_DELTA>>/,
            );
            if (match) {
              try {
                const delta = JSON.parse(match[1]);
                const pending = pendingToolCalls[delta.toolCallId];
                if (pending && delta.argsTextDelta) {
                  pending.args += delta.argsTextDelta;
                }
              } catch {
                // Ignore parsing errors
              }
            }
            continue;
          }

          // Check for tool result (triggers navigation with accumulated args)
          if (chunk.includes("<<TOOL_RESULT>>")) {
            const match = chunk.match(/<<TOOL_RESULT>>(.+?)<<\/TOOL_RESULT>>/);
            if (match) {
              try {
                const result = JSON.parse(match[1]);
                const pending = pendingToolCalls[result.toolCallId];
                if (pending?.toolName === "navigateToExploration") {
                  // Parse accumulated args and navigate
                  try {
                    const args = pending.args ? JSON.parse(pending.args) : {};
                    handleNavigateToExploration(args);
                  } catch {
                    console.warn(
                      "[VLChat] Failed to parse accumulated tool args:",
                      pending.args,
                    );
                  }
                }
              } catch {
                // Ignore parsing errors
              }
            }
            continue;
          }

          // Check for legacy tool calls format
          if (chunk.includes("[TOOL_CALL:")) {
            const match = chunk.match(/\[TOOL_CALL:(.*?)\]/);
            if (match) {
              try {
                const toolCall = JSON.parse(match[1]);
                if (toolCall.toolName === "navigateToExploration") {
                  handleNavigateToExploration(toolCall.args || {});
                }
              } catch {
                // Ignore parsing errors
              }
            }
            continue;
          }

          // Regular text content - add artificial delay for smoother visual effect
          // Clear thinking state when first text arrives
          setIsThinking(false);
          // Process character by character with delay for 2x slower appearance
          for (const char of chunk) {
            fullText += char;

            // Update the assistant message with accumulated text
            // eslint-disable-next-line no-loop-func
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      content: [{ type: "text", text: fullText }],
                    }
                  : m,
              ),
            );

            // Small delay per character for typewriter effect
            await new Promise((resolve) => setTimeout(resolve, 0.0005));
          }
        }
      } catch (error) {
        console.error("[VLChat] Streaming error:", error);
        // Update message to show error
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content: [
                    {
                      type: "text",
                      text: "Sorry, an error occurred. Please try again.",
                    },
                  ],
                }
              : m,
          ),
        );
      } finally {
        setIsRunning(false);
        // Hide tool status immediately after streaming completes
        hideToolStatus();
      }
    },
    [
      threadId,
      datasetId,
      handleNavigateToExploration,
      updateToolStatus,
      hideToolStatus,
    ],
  );

  const runtime = useExternalStoreRuntime({
    messages,
    isRunning,
    convertMessage,
    onNew,
    setMessages: (newMessages) => {
      // Convert ThreadMessageLike back to our Message format
      setMessages(
        newMessages.map((m) => ({
          id: (m as any).id || uuidv4(),
          role: m.role as "user" | "assistant" | "system",
          content: (Array.isArray(m.content)
            ? m.content
            : [{ type: "text", text: String(m.content) }]) as ContentPart[],
          createdAt: (m as any).createdAt,
        })),
      );
    },
  });

  const toolStatusContextValue = React.useMemo(
    () => ({ toolStatuses, showToolStatus, isThinking }),
    [toolStatuses, showToolStatus, isThinking],
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ToolStatusContext.Provider value={toolStatusContextValue}>
        {children}
      </ToolStatusContext.Provider>
    </AssistantRuntimeProvider>
  );
};

// ============================================================================
// VLChat Wrapper
// ============================================================================

interface VLChatWrapperProps {
  children: React.ReactNode;
}

export const VLChatWrapper: React.FC<VLChatWrapperProps> = ({ children }) => {
  const { datasetId } = useParams();
  const isEnabled = useFeatureFlagService(VL_CHAT).enabled;

  // Get or create thread ID for the current dataset
  // Uses localStorage so threads persist across browser sessions
  const getThreadId = (datasetId: string | undefined) => {
    const threadIdKey = datasetId
      ? `vl_chat_thread_id_${datasetId}`
      : "vl_chat_thread_id_no_dataset";

    let id = localStorage.getItem(threadIdKey);

    if (!id) {
      id = uuidv4();
      localStorage.setItem(threadIdKey, id);
    }

    return id;
  };

  // Thread management state
  const [threadId, setThreadId] = useState<string>(() =>
    getThreadId(datasetId),
  );

  // Update thread ID when dataset changes
  useEffect(() => {
    const newThreadId = getThreadId(datasetId);
    if (newThreadId !== threadId) {
      setThreadId(newThreadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);

  // Function to create new thread
  const createNewThread = () => {
    const threadIdKey = datasetId
      ? `vl_chat_thread_id_${datasetId}`
      : "vl_chat_thread_id_no_dataset";

    const newId = uuidv4();
    localStorage.setItem(threadIdKey, newId);
    setThreadId(newId);
  };

  // If VL Chat is disabled, just render children
  if (!isEnabled) {
    return <>{children}</>;
  }

  return (
    <VLChatRuntimeProvider threadId={threadId} datasetId={datasetId}>
      <ThreadContext.Provider value={{ threadId, createNewThread, datasetId }}>
        {children}
      </ThreadContext.Provider>
    </VLChatRuntimeProvider>
  );
};
